import BaseService from '../../base/base_service';
import _ from '../../utils/underscore';
import DOM from '../../utils/dom';
import {OVERLAY_EVENTS_SELECTORS_ID} from './overlay_managers_const';
import {PRIORITY_LEVEL, PRIORITY_LEVEL_ZINDEX_MAPPER, WRAPPER_DEFAULT_INDEX} from './overlay_manager_levels';
import OverlayComponentController from '../../components/overlay/overlay_component_controller';
import OverlayWrapperComponentController, { IOverlayWrapperComponentController } from '../../components/overlay/overlay_wrapper_component_controller';
import {SYSTEM_EVENTS} from '../../events/events';
import OverlayException from '../../exceptions/overlay/overlay_exception';
import QueueModel from './queue_model';
import IOverlayManager, { IOverlayData, OverlayQueuesMap } from './overlay_manager_service_interface';
import { action, observable, runInAction, makeObservable } from "mobx";

class OverlayManagerService extends BaseService implements IOverlayManager{
  static CLASS: string = 'OverlayManagerService';
  static OVERLAY_ID: string = OVERLAY_EVENTS_SELECTORS_ID.OVERLAY_CGP_ID;
  private _counter: number = 0;

  private _overlayQueues: OverlayQueuesMap = {};

  private _overlayWrapper: IOverlayWrapperComponentController = null;

  constructor() {
    super();

    makeObservable<OverlayManagerService, "_overlayQueues" | "_add" | "_setOverlayWrapperZIndex" | "_initQueues">(this, {
      _overlayQueues: observable,
      destroy: action.bound,
      destroyAll: action.bound,
      _add: action.bound,
      _setOverlayWrapperZIndex: action.bound,
      _initQueues: action.bound
    });
  }

  init(params) {
    super.init(params);

    this._initQueues();
    this._subscribe();
  }

  public start(overlay: IOverlayData): void {
    overlay.instance.start();

    if (!overlay.el || !_.isElement(overlay.el)) {
      this.logger.error('Could not load overlay');
      throw OverlayException('Setting current dom element on overlay failed --> ' + overlay.el);
    }

    this.logger.debug('Appending overlay into the DOM body!');
    overlay.instance.appendBody(overlay.el);

    if(PRIORITY_LEVEL_ZINDEX_MAPPER[overlay.priority] > PRIORITY_LEVEL_ZINDEX_MAPPER[PRIORITY_LEVEL.GAME_FRAME]) {
      this.serviceFactory.gameService().stopCurrentGameAutoplay();
    }

    DOM.addStyle(overlay.instance.getEl(), 'z-index', PRIORITY_LEVEL_ZINDEX_MAPPER[overlay.priority].toString());
  }


  _destroyOverlayWrapper() {
    // destroy overlay wrapper
    if (this._overlayWrapper) {
      this._overlayWrapper.getEl().remove();
      this._overlayWrapper = null;
    }
  }

  //Remove the overlay element from the screen and DOM.
  destroy(overlayToDestroy, closeParams?: any): void {
    if (_.isUndefined(overlayToDestroy)) {
      return;
    }

    this.serviceFactory.gameService().continuePlayCurrentGame();

    const overlay = DOM.$$(OverlayManagerService.OVERLAY_ID + overlayToDestroy.id);
    if (!_.isNull(overlay)) {
      overlay.remove();
      this._setOverlayWrapperZIndex();
      // Fire close callback
      if (_.isFunction(overlayToDestroy.closeCallback)) {
        overlayToDestroy.closeCallback(closeParams);
      }
      if (_.isFunction(overlayToDestroy.cleanResources)) {
        overlayToDestroy.cleanResources();
      }

      if (false === this.isActiveOverlayExists()) {
        DOM.removeClass(DOM.getBody(), 'cgp-overlay-open');
        this._destroyOverlayWrapper();
      }
    }
  }

  //Destroy all overlays.
  destroyAll(): void {
    Object.keys(this._overlayQueues).forEach((key) => {
      const queue = this._overlayQueues[key];
      while(0 < queue.size()) {
        this.destroy(queue.dequeue());
      }
    });
  }


  /**
   * Create overlay options for component
   * @param component
   * @param overlayOptions
   * @returns {*}
   * @private
   */
  private _createComponentOverlayOptions(component): any {
    const componentOptions = Object.assign({}, {
      openCallback: component.onAppendedToDOM.bind(component),
      cleanResources: component.shutDown.bind(component),
      el: component.getEl()
    }, component.getOverlayClasses());
    return componentOptions;
  }

  private _subscribe(): void {
    this.onSystemEvent(SYSTEM_EVENTS.OPEN_OVERLAY, (overlayOptions) => {
      if (!overlayOptions.priority) {
        overlayOptions.priority = PRIORITY_LEVEL.BASE;
      }

      if (!overlayOptions.id) {
        overlayOptions.id = this._counter++;
      }
      if (overlayOptions.component) {
        const componentOptions = this._createComponentOverlayOptions(overlayOptions.component);
        overlayOptions = Object.assign({}, componentOptions, overlayOptions);
      }

      const overlay = this._createOverlay(overlayOptions);
      if (!this._overlayWrapper && true === this.isActiveOverlayExists()) {
        // create overlay wrapper
        this._overlayWrapper = OverlayWrapperComponentController.create();
        this._overlayWrapper.start();
        DOM.addClass(DOM.getBody(), 'cgp-overlay-open');
      }
      this.start(overlay);
      this._setOverlayWrapperZIndex();
    });

    this.onSystemEvent(SYSTEM_EVENTS.CLOSE_OVERLAY, (params) => {
      const priority = params.priority || PRIORITY_LEVEL.BASE;
      const key = this.getPriorityNameByValue(priority);
      runInAction(() => {
        this.destroy(this._overlayQueues[key].dequeue(), params.closeParams);
      });

    });
    this.onSystemEvent(SYSTEM_EVENTS.CLOSE_ALL_OVERLAY, () => {
      this.destroyAll();
    });

    this.onSystemEvent(SYSTEM_EVENTS.OVERLAY_CLOSE_EXPIRED, (overlayId) => {
      const overlay = this._getOverlayById(overlayId);
      if (overlay) {
        this.destroy(overlay);
      }
    });

    this.onSystemEvent(SYSTEM_EVENTS.OVERLAY_OPEN_AWAIT, (overlayId) => {
      const overlay = this._getOverlayById(overlayId);
      if (overlay) {
        overlay.isActive = true;
        overlay.instance.changeState(true);
        if (!this._overlayWrapper) {
          // create overlay wrapper
          this._overlayWrapper = OverlayWrapperComponentController.create();
          this._overlayWrapper.start();
          DOM.addClass(DOM.getBody(), 'cgp-overlay-open');
          this._setOverlayWrapperZIndex();
        }
      }
    });
  }

  //Get if at least one overlay instance is active
  public isActiveOverlayExists(): boolean {
    let isShow = false;
    Object.keys(this._overlayQueues).forEach((key) => {
      if (this._overlayQueues[key].size() > 0) {
        if (false === isShow) {
          const activeOverlay = this._overlayQueues[key].queueList.filter((overlayInstance) => {
            return overlayInstance.isActive;
          });
          isShow = activeOverlay.length > 0;
        }
      }
    });

    return isShow;
  }


  private _createOverlay(overlayOptions): IOverlayData {
    if (_.isNull(overlayOptions.el)) {
      return null;
    }

    const newOverlay: IOverlayData = {
      id : (overlayOptions.id || 0),
      instance: OverlayComponentController.create(overlayOptions),
      el: (overlayOptions.el || null),
      priority: (overlayOptions.priority || 0),
      cleanResources: overlayOptions.cleanResources,
      closeCallback: overlayOptions.closeCallback,
      awaitForDisplay: overlayOptions.awaitForDisplay || false,
      isActive: overlayOptions.awaitForDisplay !== true
    };

    return this._add(newOverlay);
  }

  private _getOverlayById(overlayId): IOverlayData {
    let foundOverlay;
    Object.keys(this._overlayQueues).forEach((key) => {
      const queue = this._overlayQueues[key];
      if (foundOverlay) {
        return;
      }
      foundOverlay = queue.queueList.find((overlay) => {
        return overlay.id === overlayId;
      });
      if (foundOverlay) {
        return;
      }
    });

    return foundOverlay;
  }

  private _add(newOverlay: IOverlayData): IOverlayData {
    if (!newOverlay.priority) {
      this.logger.error('Overlay priority is not defined. Overlay creation has failed!');
      return null;
    }

    const key = this.getPriorityNameByValue(newOverlay.priority.toString());
    if (!key) {
      this.logger.error(`Overlay priority ${newOverlay.priority} does not exist. Overlay creation has failed!`);
      return null;
    }
    // clean same level overlays
    while (this._overlayQueues[key].size() > 0) {
      this.destroy(this._overlayQueues[key].dequeue());
    }

    this._overlayQueues[key].queue(newOverlay);

    return newOverlay;
  }


  //Find and set z-index for overlay wrapper to be the equal less one from the hightest overlay indexes
  private _setOverlayWrapperZIndex(): void {
    // find hightest z-index on open overlays
    if (!this._overlayWrapper) {
      return;
    }
    let index = WRAPPER_DEFAULT_INDEX;
    Object.keys(this._overlayQueues).forEach((key) => {
      const queue = this._overlayQueues[key];
      queue.queueList.forEach((overlay) => {
        if (overlay.isActive) {
          index = PRIORITY_LEVEL_ZINDEX_MAPPER[overlay.priority] > index ? PRIORITY_LEVEL_ZINDEX_MAPPER[overlay.priority] : index;
        }
      });
    });
    DOM.addStyle(this._overlayWrapper.getEl(), 'z-index', (index - 1).toString());
  }

  //Create Map of queues according to PRIORITY_LEVEL constants. One queue to each priority type
  private _initQueues(): void {
    this._overlayQueues = {};
    Object.keys(PRIORITY_LEVEL).forEach((key) => {
      this._overlayQueues[key] = QueueModel.create();
    });
  }

  //Determinate whether all overlay queues are empty
  private _isQueuesEmpty(): boolean {
    let isEmpty = true;
    Object.keys(this._overlayQueues).forEach((key) => {
      if (this._overlayQueues[key].size() > 0) {
        isEmpty = false;
      }
    });

    return isEmpty;
  }

  //Convert priority value to its original name
  public getPriorityNameByValue(priorityValue: string): string {
    return Object.keys(PRIORITY_LEVEL).find((key) => PRIORITY_LEVEL[key] === priorityValue);
  }

  public getCGPMaxOverlayZIndex(): number {
    return Math.max(...Object.values(PRIORITY_LEVEL_ZINDEX_MAPPER));
  }
}

export default OverlayManagerService;
